<template>
    <div v-if="assetLog" class="tn-node" :style="{ animationDelay: `${index * 0.06}s` }">
        <!-- Vertical line -->
        <div class="tn-line" :class="{ 'tn-line-last': isLast }"></div>

        <!-- Dot / Icon -->
        <div class="tn-dot" :class="dotColor">
            <i :class="dotIcon"></i>
        </div>

        <!-- Card -->
        <div class="tn-card">
            <div class="tn-card-head">
                <span class="tn-type-badge" :class="badgeColor">{{ eventLabel }}</span>
                <span class="tn-date"><i class="fas fa-clock"></i> {{ formatIndianDate(assetLog.createdAt) }}</span>
            </div>
            <p class="tn-message">{{ assetLog.message }}</p>

            <!-- Employee inline -->
            <div v-if="assetLog.user" class="tn-emp-inline">
                <div class="tn-emp-avatar-sm"><i class="fas fa-user"></i></div>
                <div class="tn-emp-inline-info">
                    <span class="tn-emp-inline-name">{{ assetLog.user.firstName || '' }} {{ assetLog.user.lastName || '' }}</span>
                    <span class="tn-emp-inline-code" v-if="assetLog.user.empCode">{{ assetLog.user.empCode }}</span>
                </div>
                <button class="tn-expand-btn" @click="viewMore = !viewMore">
                    <i :class="viewMore ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                    {{ viewMore ? 'Less' : 'More' }}
                </button>
            </div>

            <!-- Employee expanded details -->
            <transition name="tn-slide">
                <div v-if="assetLog.user && viewMore" class="tn-employee">
                    <div class="tn-emp-detail-grid">
                        <div class="tn-emp-detail" v-if="assetLog.user.email">
                            <i class="fas fa-envelope"></i>
                            <span>{{ assetLog.user.email }}</span>
                        </div>
                        <div class="tn-emp-detail" v-if="assetLog.user.empCode">
                            <i class="fas fa-id-card"></i>
                            <span>{{ assetLog.user.empCode }}</span>
                        </div>
                    </div>
                    <button class="tn-emp-link" @click="$router.push(`/employees/edit/${assetLog.user.id}`)">
                        <i class="fas fa-external-link-alt"></i> View Profile
                    </button>
                </div>
            </transition>

            <!-- Chain connector arrow -->
            <div class="tn-connector" v-if="!isLast">
                <i class="fas fa-long-arrow-alt-down"></i>
            </div>
        </div>
    </div>
</template>

<script>
import { formatIndianDate } from "../../utils/common"

const TYPE_MAP = {
    allocat: { icon: 'fas fa-hand-holding', label: 'Allocated', dot: 'tn-dot-green', badge: 'tn-badge-green' },
    return: { icon: 'fas fa-undo-alt', label: 'Returned', dot: 'tn-dot-amber', badge: 'tn-badge-amber' },
    repair: { icon: 'fas fa-wrench', label: 'Repair', dot: 'tn-dot-purple', badge: 'tn-badge-purple' },
    dispos: { icon: 'fas fa-trash', label: 'Disposed', dot: 'tn-dot-red', badge: 'tn-badge-red' },
    creat: { icon: 'fas fa-plus-circle', label: 'Created', dot: 'tn-dot-blue', badge: 'tn-badge-blue' },
    transfer: { icon: 'fas fa-exchange-alt', label: 'Transferred', dot: 'tn-dot-teal', badge: 'tn-badge-teal' },
}

export default {
    data() { return { viewMore: false } },
    props: {
        assetLog: { type: Object, default: null },
        index: { type: Number, default: 0 },
        isLast: { type: Boolean, default: false },
    },
    computed: {
        typeInfo() {
            const msg = ((this.assetLog.message || '') + ' ' + ((this.assetLog.asset_log_type && this.assetLog.asset_log_type.name) || '')).toLowerCase()
            for (const [key, val] of Object.entries(TYPE_MAP)) {
                if (msg.includes(key)) return val
            }
            return { icon: 'fas fa-circle', label: 'Event', dot: 'tn-dot-gray', badge: 'tn-badge-gray' }
        },
        dotIcon() { return this.typeInfo.icon },
        dotColor() { return this.typeInfo.dot },
        badgeColor() { return this.typeInfo.badge },
        eventLabel() { return this.typeInfo.label },
    },
    methods: { formatIndianDate },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.tn-node { position:relative; display:flex; align-items:flex-start; gap:0; margin-bottom:0; animation:tnSlide .3s ease both; }
@keyframes tnSlide { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }

/* Vertical line */
.tn-line { position:absolute; left:-32px; top:0; bottom:0; width:2px; background:$gray200; }
.tn-line-last { bottom:50%; }

/* Dot */
.tn-dot { position:absolute; left:-41px; top:16px; width:20px; height:20px; border-radius:$radiusFull; display:flex; align-items:center; justify-content:center; font-size:9px; z-index:1; border:2px solid white; box-shadow:0 0 0 2px $gray200; }
.tn-dot-green { background:#dcfce7; color:#16a34a; box-shadow:0 0 0 2px #bbf7d0; }
.tn-dot-amber { background:#fef3c7; color:#d97706; box-shadow:0 0 0 2px #fde68a; }
.tn-dot-purple { background:#ede9fe; color:#7c3aed; box-shadow:0 0 0 2px #ddd6fe; }
.tn-dot-red { background:#fee2e2; color:#dc2626; box-shadow:0 0 0 2px #fecaca; }
.tn-dot-blue { background:#dbeafe; color:#2563eb; box-shadow:0 0 0 2px #bfdbfe; }
.tn-dot-teal { background:#ccfbf1; color:#0d9488; box-shadow:0 0 0 2px #99f6e4; }
.tn-dot-gray { background:$gray100; color:$gray400; box-shadow:0 0 0 2px $gray200; }

/* Card */
.tn-card { flex:1; background:white; border:1px solid $gray100; border-radius:10px; padding:14px 16px; margin-bottom:12px; box-shadow:$shadowSm; transition:all $transitionFast; &:hover { box-shadow:$shadowMd; border-color:$gray200; } }
.tn-card-head { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; flex-wrap:wrap; }
.tn-type-badge { font-size:10px; font-weight:700; padding:3px 10px; border-radius:$radiusFull; letter-spacing:0.2px; }
.tn-badge-green { background:#f0fdf4; color:#16a34a; }
.tn-badge-amber { background:#fffbeb; color:#d97706; }
.tn-badge-purple { background:#f5f3ff; color:#7c3aed; }
.tn-badge-red { background:#fef2f2; color:#dc2626; }
.tn-badge-blue { background:#eff6ff; color:#2563eb; }
.tn-badge-teal { background:#f0fdfa; color:#0d9488; }
.tn-badge-gray { background:$gray50; color:$gray500; }
.tn-date { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.tn-message { font-size:13px; font-weight:600; color:$gray700; margin:0 0 6px; line-height:1.45; }

/* Expand */
.tn-expand-btn { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; background:white; color:$primaryColor; transition:all $transitionFast; font-family:inherit; i { font-size:9px; } &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.tn-slide-enter-active, .tn-slide-leave-active { transition:all .2s ease; overflow:hidden; }
.tn-slide-enter, .tn-slide-leave-to { opacity:0; max-height:0; margin-top:0; }
.tn-slide-enter-to, .tn-slide-leave { opacity:1; max-height:200px; margin-top:10px; }

/* Employee Inline */
.tn-emp-inline { display:flex; align-items:center; gap:8px; background:$gray50; border:1px solid $gray100; border-radius:8px; padding:8px 12px; margin-top:2px; }
.tn-emp-avatar-sm { width:28px; height:28px; border-radius:$radiusFull; background:$primaryLight; color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:11px; flex-shrink:0; }
.tn-emp-inline-info { flex:1; display:flex; align-items:center; gap:8px; min-width:0; }
.tn-emp-inline-name { font-size:12.5px; font-weight:700; color:$gray800; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tn-emp-inline-code { font-size:10.5px; font-weight:500; color:$gray400; background:white; border:1px solid $gray200; padding:1px 7px; border-radius:$radiusFull; white-space:nowrap; }

/* Employee Expanded */
.tn-employee { background:$gray50; border:1px solid $gray100; border-radius:8px; padding:12px; margin-top:10px; }
.tn-emp-detail-grid { display:flex; flex-wrap:wrap; gap:8px 16px; margin-bottom:8px; }
.tn-emp-detail { font-size:11.5px; color:$gray500; font-weight:500; display:inline-flex; align-items:center; gap:5px; i { font-size:10px; color:$gray400; } }
.tn-emp-link { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; background:white; color:$primaryColor; transition:all $transitionFast; font-family:inherit; i { font-size:9px; } &:hover { background:$primaryLight; } }

/* Connector arrow */
.tn-connector { text-align:center; color:$gray200; font-size:16px; margin-top:6px; }

@media(max-width:480px) {
    .tn-dot { left:-33px; width:18px; height:18px; font-size:8px; }
    .tn-card { padding:10px 12px; }
}
</style>
